import { useEffect, useState } from "react";
import { authService } from "../../services/api";
import { sortBotsByDisplayOrder } from "../../utils/bots";
import { useNotification } from "../Notifications/NotificationContext";
import { useBotPhotos } from "../../hooks/useBotPhotos";
import BotAvatar from "../BotAvatar/BotAvatar";
import "./ManageBotsModal.css";

export default function ManageBotsModal({ user, open, onClose, onSaved }) {
  const { showError, showSuccess } = useNotification();

  const [botsState, setBotsState] = useState([]);
  const [botsDirty, setBotsDirty] = useState(false);
  const photoUrls = useBotPhotos(botsState);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (!open || !user) return;
    if (Array.isArray(user.bots)) {
      setBotsState(sortBotsByDisplayOrder(user.bots));
      setBotsDirty(false);
      setDragIndex(null);
    } else {
      setBotsState([]);
      setBotsDirty(false);
      setDragIndex(null);
    }
  }, [open, user]);

  if (!open) return null;

  const handleToggleVisibility = (botId) => {
    setBotsState(prev => {
      const next = prev.map(bot =>
        bot.id === botId ? { ...bot, is_hidden: !bot.is_hidden } : bot
      );
      return next;
    });
    setBotsDirty(true);
  };

  const moveBot = (from, to) => {
    setBotsState(prev => {
      if (from === to || from == null || to == null) return prev;
      if (from < 0 || from >= prev.length) return prev;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setBotsDirty(true);
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
  };

  const handleDrop = (event, index) => {
    event.preventDefault();
    if (dragIndex == null) return;
    moveBot(dragIndex, index);
    setDragIndex(null);
  };

  const handleSave = async () => {
    if (!botsState.length) return;
    setSaving(true);
    try {
      const payload = botsState.map((bot) => ({
        id: bot.id,
        is_hidden: Boolean(bot.is_hidden),
      }));
      await authService.bots.manage(payload);
      setBotsDirty(false);
      showSuccess("Bots atualizados com sucesso!");
      if (onSaved) {
        await onSaved();
      }
      onClose();
    } catch (err) {
      console.warn("[ManageBotsModal] manage_bots error:", err?.response?.data || err?.message);
      showError(
        "Erro ao atualizar bots. " +
          (err?.response?.data?.error || err?.message || "")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (Array.isArray(user?.bots)) {
      setBotsState(sortBotsByDisplayOrder(user.bots));
      setBotsDirty(false);
      setDragIndex(null);
    }
    onClose();
  };

  const handleResetOrder = () => {
    if (Array.isArray(user?.bots)) {
      setBotsState(sortBotsByDisplayOrder(user.bots));
      setBotsDirty(false);
      setDragIndex(null);
    }
  };

  return (
    <div className="manage-bots-modal-overlay" onClick={handleCancel}>
      <div
        className="manage-bots-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="manage-bots-modal-header">
          <h3>
           Gerenciar Bots
          </h3>
          <button
            type="button"
            className="manage-bots-close"
            onClick={handleCancel}
          >
            &times;
          </button>
        </div>

        {(!user?.bots || user.bots.length === 0) ? (
          <p className="manage-bots-empty">
            Você ainda não possui bots cadastrados.
          </p>
        ) : (
          <>
            <p className="manage-bots-description">
              Clique, segure e arraste (grab and release) para reordenar.
              Use o ícone de olho para ocultar bots das listas da plataforma.
            </p>

            <div className="manage-bots-list">
              {botsState.map((bot, index) => {
                const isHidden = Boolean(bot.is_hidden);
                const label = bot.bot_username || bot.bot_name || `Bot #${bot.id}`;
                const isDragging = dragIndex === index;

                return (
                  <div
                    key={bot.id}
                    className={`manage-bot-row ${isDragging ? "dragging" : ""}`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="manage-bot-left">
                      <span className="manage-bot-grab" title="Arraste para reordenar">
                        <i className="fas fa-grip-vertical" />
                      </span>
                      <BotAvatar photoUrl={photoUrls[bot.id]} size={28} style={{ opacity: isHidden ? 0.45 : 1 }} />
                      <div className="manage-bot-text">
                        <p className={`manage-bot-name ${isHidden ? "manage-bot-name-hidden" : ""}`}>
                          {label}
                        </p>
                        <div className="manage-bot-sub">
                          {isHidden ? "Oculto das listas" : "Visível nas listas"}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="manage-bot-visibility"
                      onClick={() => handleToggleVisibility(bot.id)}
                    >
                      <i
                        className={isHidden ? "fas fa-eye-slash" : "fas fa-eye"}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="manage-bots-footer">
              {/* <button
                type="button"
                className="btn-secondary"
                disabled={saving}
                onClick={handleResetOrder}
              >
                Resetar ordem
              </button> */}
              <div className="manage-bots-footer-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={saving}
                  onClick={handleCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={!botsDirty || saving}
                  onClick={handleSave}
                >
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin me-2" /> Salvando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2" /> Salvar
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
